/**
 * Image upload service — Plate edition.
 *
 * Owned by the PlateEditor module so the editor doesn't reach across
 * module boundaries into the legacy TextEditor's plugin directory. The
 * legacy editor keeps its own copy in src/common/TextEditor/plugins/ for
 * back-compat; the two are intentional duplicates so neither editor
 * depends on the other's internals.
 *
 * Lifecycle:
 *   ON PASTE  → image stays as base64 in editor state
 *   ON SAVE   → base64 images uploaded to S3, replaced with docId
 *   ON LOAD   → docIds resolved to presigned S3 URLs (bulk + per-image cache)
 *   ON DELETE → removed images soft-deleted server-side
 *
 * Note on shared state: this module owns an in-memory cache + pending-
 * upload map. The legacy copy in TextEditor/plugins/ has its OWN state.
 * If both editors are mounted simultaneously they will cache the same
 * docIds twice — wasteful but correct. Consolidate later by hoisting the
 * mutable state into a third module both copies import.
 */

import {
    getSignedUrl,
    uploadFile,
    saveDocMetaDataInDocService,
    getPresignedUrl,
    getPresignedUrlBulk,
    deleteEditorImages,
} from '../../../modules/Company/UploadJourney/components/UploadCard/utils/api/helpers';
import { Descendant } from 'slate';
import { PROMISE_STATES } from '../../../utils/axios-interceptor';
import { toast } from 'react-toastify';

interface ImageInfo {
    path: number[];
    url: any;
    type: 'base64' | 'docId';
}

const EDITOR_IMAGE_CATEGORY_ID = 100649;
const EDITOR_IMAGE_FLOW_ID = 16;
const CACHE_TTL_MS = 10 * 60 * 1000;

export const isBase64 = (url: any): boolean => typeof url === 'string' && url.startsWith('data:image/');

export const isDocId = (url: any): boolean => {
    if (typeof url === 'number') return true;
    if (typeof url === 'string') return /^\d+$/.test(url) && !isBase64(url);
    return false;
};

/**
 * A "remote-URL image" is an image node whose `url` is an absolute http(s) URL
 * that is neither a base64 data URI nor a docId. These come from pasting an
 * <img> off the web (e.g. a Google Docs `googleusercontent.com` URL). They
 * render until the source URL expires, then break — so they must be fetched
 * and re-stored in our S3 like a pasted base64 image.
 */
export const isRemoteImageUrl = (url: any): boolean => typeof url === 'string' && /^https?:/i.test(url) && !isBase64(url) && !isDocId(url);

/**
 * Walk a Slate fragment and collect the paths of every image node whose `url`
 * is a remote http(s) URL. Pure — used by the paste handler to find images
 * that need re-hosting after insert. Returns each match's slate path + url.
 */
export function collectRemoteImageNodes(nodes: any[], parentPath: number[] = []): { path: number[]; url: string }[] {
    if (!Array.isArray(nodes)) return [];
    const found: { path: number[]; url: string }[] = [];
    nodes.forEach((node: any, i: number) => {
        const path = [...parentPath, i];
        if (node?.type === 'image' && isRemoteImageUrl(node.url)) found.push({ path, url: node.url });
        if (Array.isArray(node?.children)) found.push(...collectRemoteImageNodes(node.children, path));
    });
    return found;
}

const str = (v: any): string => String(v);

function collectImages(nodes: any[], parentPath: number[] = []): ImageInfo[] {
    if (!Array.isArray(nodes)) return [];
    const images: ImageInfo[] = [];
    nodes.forEach((node: any, i: number) => {
        const path = [...parentPath, i];
        if (node.type === 'image' && node.url) {
            if (isBase64(node.url)) images.push({ path, url: node.url, type: 'base64' });
            else if (isDocId(node.url)) images.push({ path, url: node.url, type: 'docId' });
        }
        if (node.children) images.push(...collectImages(node.children, path));
    });
    return images;
}

function setNodeUrl(root: any[], path: number[], url: string) {
    let node: any = root;
    for (let i = 0; i < path.length - 1; i++) {
        node = node[path[i]]?.children ?? node[path[i]];
    }
    const target = node[path[path.length - 1]];
    if (target?.type === 'image') target.url = url;
}

function base64ToFile(dataUrl: string, fileName: string): File {
    const [header, data] = dataUrl.split(',');
    const mime = header.match(/:(.*?);/)?.[1] ?? 'image/png';
    const bytes = Uint8Array.from(atob(data), (c) => c.charCodeAt(0));
    return new File([bytes], `${fileName}.${mime.split('/')[1] || 'png'}`, { type: mime });
}

export async function uploadSingleImage(base64Url: string, source: string): Promise<string> {
    const orgId = localStorage.getItem('user_org_id') || '';
    const file = base64ToFile(base64Url, `${source}-${Date.now()}`);

    const signRes = await getSignedUrl(
        { file },
        {
            assesseeOrgId: orgId,
            categoryId: EDITOR_IMAGE_CATEGORY_ID,
            mnemosyneOrgId: orgId,
        },
    );
    if (!signRes?.signedUrl) throw new Error('Failed to get signed URL');

    await uploadFile(file, signRes.signedUrl);

    const doc = await saveDocMetaDataInDocService({
        input: {
            categoryId: EDITOR_IMAGE_CATEGORY_ID,
            mnemosyneOrgId: orgId,
            fileName: file.name,
            fileSizeKb: file.size / 1000,
            flowId: EDITOR_IMAGE_FLOW_ID,
            fileType: file.type,
        },
        orgId,
    });
    if (!doc?.id) throw new Error('Failed to get docId');
    return String(doc.id);
}

const cache: Record<string, { url: string; exp: number }> = {};
let bulkPromise: Promise<Record<string, string>> | null = null;

const pendingUploads: Map<string, Promise<unknown>> = new Map();

export function registerPendingUpload(placeholderId: string, promise: Promise<unknown>) {
    pendingUploads.set(placeholderId, promise);
    promise.finally(() => {
        if (pendingUploads.get(placeholderId) === promise) pendingUploads.delete(placeholderId);
    });
}

export async function awaitAllPendingUploads(): Promise<void> {
    if (pendingUploads.size === 0) return;
    await Promise.allSettled(Array.from(pendingUploads.values()));
}

function cacheGet(id: string): string | null {
    const entry = cache[id];
    return entry && entry.exp > Date.now() ? entry.url : null;
}

function cacheSet(id: string, url: string) {
    cache[id] = { url, exp: Date.now() + CACHE_TTL_MS };
}

export function collectDocIdsFromSlate(value: Descendant[]): string[] {
    if (!value?.length) return [];
    return collectImages(value)
        .filter((i) => i.type === 'docId')
        .map((i) => str(i.url));
}

/**
 * Process editor content before saving to DB:
 * 1. Upload new base64 images to S3 (parallel), replace with docId
 * 2. Delete removed images server-side (diff current vs original)
 */
export async function processImagesBeforeSave(
    value: Descendant[],
    originalDocIds: string[] = [],
    source = 'editor',
): Promise<Descendant[]> {
    if (!value?.length) return value;

    // Wait for any lazy uploads kicked off mid-edit.
    await awaitAllPendingUploads();

    const images = collectImages(value);
    const base64Images = images.filter((i) => i.type === 'base64');

    let cloned = value;

    if (base64Images.length > 0) {
        cloned = JSON.parse(JSON.stringify(value));
        const results = await Promise.allSettled(
            base64Images.map(async (img) => ({
                path: img.path,
                docId: await uploadSingleImage(img.url, source),
            })),
        );
        let failedCount = 0;
        results.forEach((r) => {
            if (r.status === PROMISE_STATES.FULFILLED) setNodeUrl(cloned as any[], r.value.path, r.value.docId);
            else failedCount += 1;
        });
        if (failedCount > 0) {
            console.error(`[imageUploadService] ${failedCount} image upload(s) failed during save (source: ${source})`);
            toast.error(`${failedCount} image${failedCount > 1 ? 's' : ''} could not be uploaded. Please retry.`);
        }
    }

    if (originalDocIds.length > 0) {
        const currentIds = new Set(collectDocIdsFromSlate(cloned));
        const removed = originalDocIds.filter((id) => !currentIds.has(id));
        if (removed.length > 0) deleteEditorImages(removed.map(Number));
    }

    return cloned;
}

/** Fetch a single presigned URL. Piggybacks on an in-flight bulk fetch if any. */
export async function fetchImageUrl(docId: string): Promise<string> {
    if (bulkPromise) {
        const result = await bulkPromise;
        if (result[docId]) return result[docId];
    }

    const cached = cacheGet(docId);
    if (cached) return cached;

    const res = await getPresignedUrl({ docId: Number(docId) }, false);
    if (!res?.uri) throw new Error(`Failed to fetch presigned URL for docId: ${docId}`);
    cacheSet(docId, res.uri);
    return res.uri;
}

// Deep clone nodes and replace all docId image urls using a lookup map
export function replaceImageUrls(nodes: any[], urlMap: Record<string, string>): any[] {
    const cloned = JSON.parse(JSON.stringify(nodes));
    const walk = (list: any[]) => {
        for (const node of list) {
            if (node.type === 'image' && isDocId(node.url) && urlMap[str(node.url)]) {
                node.url = urlMap[str(node.url)];
            }
            if (node.children) walk(node.children);
        }
    };
    walk(cloned);
    return cloned;
}

// Fetch image from URL and convert to base64 data URL (for PDF rendering)
async function urlToBase64(url: string): Promise<string> {
    const blob = await (await fetch(url)).blob();
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onloadend = () => resolve(r.result as string);
        r.onerror = reject;
        r.readAsDataURL(blob);
    });
}

/**
 * Resolve docId images to base64 for PDF generation.
 * @react-pdf/renderer can't fetch from S3 presigned URLs (CORS),
 * so we fetch the images ourselves and inline them as base64 data URLs.
 */
export async function resolveDocIdsForPdf(value: Descendant[]): Promise<Descendant[]> {
    if (!value?.length) return value;

    const docIds = collectDocIdsFromSlate(value);
    if (docIds.length === 0) return value;

    // Get fresh presigned URLs (skip cache — cached URLs may have expired for PDF use)
    const freshUrls: Record<string, string> = {};
    const bulk = await getPresignedUrlBulk(docIds.map(Number));
    for (const [id, url] of Object.entries(bulk)) {
        freshUrls[String(id)] = url;
    }

    // Fetch actual image bytes and convert to base64 (parallel)
    const base64Map: Record<string, string> = {};
    await Promise.all(
        docIds.map(async (id) => {
            const presignedUrl = freshUrls[id];
            if (!presignedUrl) {
                console.warn(`[resolveDocIdsForPdf] No presigned URL for docId: ${id}`);
                return;
            }
            try {
                base64Map[id] = await urlToBase64(presignedUrl);
            } catch (e) {
                console.error(`[resolveDocIdsForPdf] Failed to fetch image for docId: ${id}`, e);
            }
        }),
    );

    if (Object.keys(base64Map).length === 0 && docIds.length > 0) {
        console.warn('[resolveDocIdsForPdf] No images resolved. docIds:', docIds, 'freshUrls:', freshUrls);
    } else if (Object.keys(base64Map).length < docIds.length && docIds.length > 0) {
        const resolvedCount = Object.keys(base64Map).length;
        const missingIds = docIds.filter((id) => !base64Map[id]);
        console.warn(
            `[resolveDocIdsForPdf] Partial resolution: ${resolvedCount} of ${
                docIds.length
            } image(s) resolved. Missing docIds: [${missingIds.join(', ')}]`,
        );
        toast.warn(
            `${missingIds.length} image${missingIds.length > 1 ? 's' : ''} could not be loaded for PDF export and will appear missing.`,
        );
    }

    return replaceImageUrls(value, base64Map) as Descendant[];
}

/**
 * Bulk presigned URL fetch — used on editor mount. `bypassCache` skips
 * `cacheGet` so every id is re-minted fresh; the PDF exporter passes it so a
 * URL cached (10 min TTL) can't outlive the mnemosyne signature it points at
 * (see resolveImagesToPresignedUrls). Freshly minted URLs still populate the
 * cache either way, so the live editor benefits too.
 */
export async function fetchImageUrlsBulk(docIds: string[], bypassCache = false): Promise<Record<string, string>> {
    const result: Record<string, string> = {};
    const uncached: number[] = [];

    for (const id of docIds) {
        const url = bypassCache ? null : cacheGet(id);
        if (url) result[id] = url;
        else uncached.push(Number(id));
    }

    if (uncached.length === 0) return result;

    bulkPromise = (async () => {
        const bulk = await getPresignedUrlBulk(uncached);
        for (const [id, url] of Object.entries(bulk)) {
            cacheSet(String(id), url);
            result[String(id)] = url;
        }
        return result;
    })();

    try {
        return await bulkPromise;
    } finally {
        bulkPromise = null;
    }
}
